import { absolute, api } from './api';
import { speak } from './format';
import type { EpisodeDetail, Media } from '../types';

/**
 * Plays one sentence from its original episode audio (vocab notebook, review).
 * Falls back to speech synthesis when the episode media is not playable here.
 */
const mediaCache = new Map<string, Promise<Media>>();
let audio: HTMLAudioElement | null = null;
let stopAt = 0;
let frame = 0;

function mediaOf(episodeId: string): Promise<Media> {
  let p = mediaCache.get(episodeId);
  if (!p) {
    p = api<EpisodeDetail>(`/episodes/${episodeId}`).then((d) => d.episode.media);
    p.catch(() => mediaCache.delete(episodeId));
    mediaCache.set(episodeId, p);
  }
  return p;
}

export function stopClip() {
  cancelAnimationFrame(frame);
  audio?.pause();
  if ('speechSynthesis' in window) speechSynthesis.cancel();
}

export async function playClip(clip: { episodeId: string; startMs: number; endMs: number; en: string }): Promise<'original' | 'tts'> {
  stopClip();
  let media: Media | null = null;
  try {
    media = await mediaOf(clip.episodeId);
  } catch {
    media = null;
  }
  if (media?.kind !== 'file') {
    speak(clip.en);
    return 'tts';
  }
  audio ??= new Audio();
  const src = absolute(media.url);
  if (audio.src !== new URL(src, location.href).href) audio.src = src;
  const el = audio;
  stopAt = clip.endMs / 1000 + 0.15;
  const start = () => {
    el.currentTime = clip.startMs / 1000;
    void el.play().catch(() => speak(clip.en));
    const check = () => {
      if (el.currentTime >= stopAt) el.pause();
      else if (!el.paused) frame = requestAnimationFrame(check);
    };
    frame = requestAnimationFrame(check);
  };
  if (el.readyState >= 1) start();
  else el.addEventListener('loadedmetadata', start, { once: true });
  return 'original';
}
